import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff, Lock, ArrowRight } from 'lucide-react';

const LoginForm = ({ onLogin, loading = false }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) { setError('Please fill in all fields'); return; }
    try { await onLogin(formData); } catch (err) { setError(err.message || 'Login failed'); }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  return (
    <div className="rounded-2xl p-[1px]" style={{ background: 'linear-gradient(135deg, rgba(102,194,202,0.3), rgba(156,94,229,0.15), rgba(102,194,202,0.1))' }}>
      <div className="rounded-2xl px-8 py-8" style={{ background: 'rgba(22,37,56,0.85)', backdropFilter: 'blur(20px)' }}>
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
            style={{ background: 'linear-gradient(135deg, rgba(102,194,202,0.15), rgba(102,194,202,0.05))', border: '1px solid rgba(102,194,202,0.2)' }}>
            <Lock className="h-5 w-5" style={{ color: '#66C2CA' }} />
          </div>
          <h2 className="text-xl font-semibold text-white mb-1">Welcome Back</h2>
          <p className="text-sm" style={{ color: '#8B9DB6' }}>Sign in to your account</p>
        </div>

        {error && (
          <Alert className="mb-5 border-red-500/30 bg-red-500/10 rounded-xl">
            <AlertDescription className="text-red-300 text-sm">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#8B9DB6' }}>Email Address</label>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@company.com"
              disabled={loading}
              required
              data-testid="login-email-input"
              className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all"
              style={{ background: 'rgba(8,17,32,0.6)', border: '1px solid rgba(102,194,202,0.15)', focusRingColor: '#66C2CA' }}
              onFocus={e => { e.target.style.borderColor = 'rgba(102,194,202,0.5)'; e.target.style.boxShadow = '0 0 0 2px rgba(102,194,202,0.15)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(102,194,202,0.15)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#8B9DB6' }}>Password</label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={loading}
                required
                data-testid="login-password-input"
                className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none transition-all pr-10"
                style={{ background: 'rgba(8,17,32,0.6)', border: '1px solid rgba(102,194,202,0.15)' }}
                onFocus={e => { e.target.style.borderColor = 'rgba(102,194,202,0.5)'; e.target.style.boxShadow = '0 0 0 2px rgba(102,194,202,0.15)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(102,194,202,0.15)'; e.target.style.boxShadow = 'none'; }}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: '#4A5E75' }}
                onClick={() => setShowPassword(!showPassword)}
                onMouseEnter={e => e.target.style.color = '#66C2CA'}
                onMouseLeave={e => e.target.style.color = '#4A5E75'}
                disabled={loading}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button 
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 mt-2"
            style={{ 
              background: loading ? 'rgba(102,194,202,0.3)' : 'linear-gradient(135deg, #66C2CA 0%, #5EDEE5 100%)', 
              color: '#081120',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(102,194,202,0.25)'
            }}
            onMouseEnter={e => { if (!loading) e.target.style.boxShadow = '0 6px 20px rgba(102,194,202,0.4)'; }}
            onMouseLeave={e => { if (!loading) e.target.style.boxShadow = '0 4px 15px rgba(102,194,202,0.25)'; }}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#081120] border-t-transparent"></div>
                Signing in...
              </>
            ) : (
              <>Sign In <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="mt-5 text-center">
          <p className="text-xs" style={{ color: '#4A5E75' }}>Contact your administrator for account access</p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
